import * as Sentry from '@sentry/react';
import type { Dispatch } from '@reduxjs/toolkit';

import { isDeploymentUnreachableError, serverMessageOf, statusOf } from './thunk';
import type {
  OfflineModeReach,
  OfflineModeReachBound,
  OfflineModeReachChannel,
  OfflineModeReachCounts,
  OfflineModeReachDirection,
  OfflineModeReachErrorCode,
  OfflineModeReachLimit,
  OfflineModeReachQuality,
  OfflineModeReachRoster,
} from './index';
import TCClient from 'client';
import { actions } from 'redux-stores';
import { localServerRequestOptions } from 'common/utils/offlineMode/localServerAuthHeaders';

type QualityWire = {
  level?: unknown;
  denominator_trustworthy?: unknown;
  numerator_trustworthy?: unknown;
  bounds?: unknown;
  limits?: unknown;
  display_caveat?: unknown;
  safe_direction?: unknown;
};

type ReachWire = {
  direction?: unknown;
  measured?: unknown;
  generated_at?: unknown;
  counts?: unknown;
  roster?: unknown;
  channels?: unknown;
  quality?: unknown;
  unavailable_reason?: unknown;
  unavailable_code?: unknown;
  active_since?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;

const text = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const timestamp = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

export function parseBound(value: unknown): OfflineModeReachBound | undefined {
  if (value === 'at_least' || value === 'at_most' || value === 'exact') return value;
  return typeof value === 'string' && value.trim() !== '' ? 'at_least' : undefined;
}

export function parseLimits(value: unknown): OfflineModeReachLimit[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const code = text(raw.code);
    if (!code) return [];
    const limit: OfflineModeReachLimit = {
      code,
      affects: Array.isArray(raw.affects)
        ? raw.affects.filter((a): a is string => typeof a === 'string')
        : [],
    };
    const ticket = text(raw.ticket);
    if (ticket) limit.ticket = ticket;
    const severity = text(raw.severity);
    if (severity) limit.severity = severity;
    const effect = text(raw.effect);
    if (effect) limit.effect = effect;
    const body = text(raw.text);
    if (body) limit.text = body;
    return [limit];
  });
}

export function parseQuality(value: unknown): OfflineModeReachQuality | null {
  if (!isRecord(value)) return null;
  const wire = value as QualityWire;
  const level = text(wire.level);
  if (!level) return null;

  const boundsWire = isRecord(wire.bounds) ? wire.bounds : {};
  const bounds: OfflineModeReachQuality['bounds'] = {};
  (['reachable_now', 'reachable_on_rejoin', 'not_reachable', 'total'] as const).forEach((field) => {
    const bound = parseBound(boundsWire[field]);
    if (bound) bounds[field] = bound;
  });

  const quality: OfflineModeReachQuality = {
    level,
    denominatorTrustworthy: wire.denominator_trustworthy === true,
    numeratorTrustworthy: wire.numerator_trustworthy === true,
    bounds,
    limits: parseLimits(wire.limits),
  };

  const caveat = text(wire.display_caveat);
  if (caveat) quality.displayCaveat = caveat;
  const safeDirection = text(wire.safe_direction);
  if (safeDirection) quality.safeDirection = safeDirection;

  return quality;
}

export function parseCounts(value: unknown): OfflineModeReachCounts | null {
  if (!isRecord(value)) return null;
  const reachableNow = count(value.reachable_now);
  const reachableOnRejoin = count(value.reachable_on_rejoin);
  const notReachable = count(value.not_reachable);
  const total = count(value.total);
  if (
    reachableNow === null ||
    reachableOnRejoin === null ||
    notReachable === null ||
    total === null
  ) {
    return null;
  }

  const counts: OfflineModeReachCounts = { reachableNow, reachableOnRejoin, notReachable, total };
  const outsideRoster = count(value.outside_roster);
  if (outsideRoster !== null) counts.outsideRoster = outsideRoster;
  const willBeInformed = count(value.will_be_informed);
  if (willBeInformed !== null) counts.willBeInformed = willBeInformed;
  const label = text(value.label);
  if (label) counts.label = label;
  return counts;
}

export function parseRoster(value: unknown): OfflineModeReachRoster | null {
  if (!isRecord(value)) return null;
  const roster: OfflineModeReachRoster = {};
  const replicatedAt = timestamp(value.replicated_at);
  if (replicatedAt) roster.replicatedAt = replicatedAt;
  const userCount = count(value.user_count);
  if (userCount !== null) roster.userCount = userCount;
  if (typeof value.stale === 'boolean') roster.stale = value.stale;
  const mode = text(value.mode);
  if (mode) roster.mode = mode;
  return roster;
}

export function parseChannels(value: unknown): OfflineModeReachChannel[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const name = text(raw.channel);
    const basis = text(raw.basis);
    if (!name || !basis) return [];
    const channel: OfflineModeReachChannel = {
      channel: name,
      available: raw.available === true,
      basis,
    };
    const lastSuccessAt = timestamp(raw.last_success_at);
    if (lastSuccessAt) channel.lastSuccessAt = lastSuccessAt;
    const body = text(raw.text);
    if (body) channel.text = body;
    return [channel];
  });
}

export function parseReach(
  raw: unknown,
  direction: OfflineModeReachDirection
): OfflineModeReach | null {
  if (!isRecord(raw)) return null;
  const wire = raw as ReachWire;
  if (typeof wire.measured !== 'boolean') return null;

  const quality = parseQuality(wire.quality);
  if (!quality) return null;

  const counts = parseCounts(wire.counts);
  if (wire.measured && !counts) return null;

  const reach: OfflineModeReach = {
    direction,
    measured: wire.measured,
    counts: wire.measured ? counts : null,
    roster: wire.measured ? parseRoster(wire.roster) : null,
    channels: parseChannels(wire.channels),
    quality,
  };

  const generatedAt = timestamp(wire.generated_at);
  if (generatedAt) reach.generatedAt = generatedAt;
  const reason = text(wire.unavailable_reason);
  if (reason) reach.unavailableReason = reason;
  const code = text(wire.unavailable_code);
  if (code) reach.unavailableCode = code;
  const activeSince = timestamp(wire.active_since);
  if (activeSince) reach.activeSince = activeSince;

  return reach;
}

export function reachErrorCode(err: unknown): OfflineModeReachErrorCode {
  if (isDeploymentUnreachableError(err)) return 'unreachable';
  return statusOf(err) === 403 ? 'forbidden' : 'read-failed';
}

export const reachPath = (organizationId: string, direction: OfflineModeReachDirection) =>
  `/v2/organization/${organizationId}/offline-mode/reach?direction=${direction}`;

export const fetchOfflineModeReach = async (
  dispatch: Dispatch,
  organizationId: string,
  direction: OfflineModeReachDirection
) => {
  if (!organizationId) return;

  dispatch(actions.setOfflineModeReachGathering(direction));

  try {
    const res = await TCClient.httpClient.get(
      reachPath(organizationId, direction),
      localServerRequestOptions(TCClient.httpClient)
    );
    const reach = parseReach(res.data, direction);

    if (!reach) {
      Sentry.logger.error(
        Sentry.logger.fmt`[OfflineMode] Unusable reach reply for ${organizationId}`
      );
      dispatch(actions.setOfflineModeReachFailed({ code: 'unusable', direction }, organizationId));
      return;
    }

    dispatch(actions.setOfflineModeReach(reach, organizationId));
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to read reach counts`, { err });
    const code = reachErrorCode(err);
    const serverMessage = serverMessageOf(err);
    dispatch(
      actions.setOfflineModeReachFailed(
        serverMessage ? { code, serverMessage, direction } : { code, direction },
        organizationId
      )
    );
  }
};
